import { ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { css } from '@emotion/core'
import { Button, ResponsiveTableRow, Separator, H5, DangerButton } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../../common/DmcResponsiveTable'
import { PersonLink, RelationshipLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import { DotSeparator } from '../../metadata/MetaData'
import { GetAssociationRelationshipRole } from '../../relationship/relationshipUtils'
import PersonInfo from './PersonInfo'
import OwnerId from '../../common/OwnerId'
import AddRelationships from './AddRelationships'

const floatRight = css`
  float: right;
`

export default function AssociationsPanel({
  handleRemoveSelected,
  handleRestoreSelected,
  currentPerson,
  onRemove,
  onRestore,
  accessControls,
  tombstonedAssociationRelationships,
  showTombstonedRelationships,
}) {
  const { isAuthorized } = useContext(PermissionsContext)
  const { t } = useTranslation()
  const associationsRelationshipsCount = currentPerson?.relationships?.associations?.length || 0
  const tombstonedAssociationsRelationshipsCount = tombstonedAssociationRelationships
    ? tombstonedAssociationRelationships.length
    : 0
  const [relationshipsToRemove, setRelationshipsToRemove] = useState([])
  const [relationshipsToRestore, setRelationshipsToRestore] = useState([])
  return (
    <div>
      <H5 growX>
        {t('person.relationships.associationsof_plural', 'Other Relationships ({count}) of', {
          count: associationsRelationshipsCount,
        })}
        {': '}
        <PersonLink personId={currentPerson.id} personName={currentPerson.name} />
        <DotSeparator />
        {currentPerson.id}
      </H5>
      {isAuthorized('FtWritePermission') && associationsRelationshipsCount > 0 && relationshipsToRemove.length > 0 && (
        <DangerButton
          id="assocCollectiveRemove"
          Icon={ContentDelete}
          onClick={() => handleRemoveSelected(relationshipsToRemove, 'association')}
        >
          {t('action.removeAssociations', 'Remove ({count}) Associations', {
            count: relationshipsToRemove.length,
          })}
        </DangerButton>
      )}
      {associationsRelationshipsCount > 0 && (
        <DmcResponsiveCheckboxTable
          id="associationTable"
          totalColumnCount={5}
          onSelectionChange={setRelationshipsToRemove}
          atSizeColumns={{
            default: {
              labels: [
                t('relationship.role.associate', 'Associate'),
                t('role.label', 'Role'),
                t('owner', 'Owner'),
                t('person.relationships.relationshipid', 'Relationship ID'),
                '',
              ],
              columnWidths: [4, 2, 3, 2, 1],
            },
          }}
        >
          {currentPerson.relationships.associations &&
            currentPerson.relationships.associations.map((relationship) => {
              const usePerson1 = relationship.person1.personId !== currentPerson.id
              const associate = usePerson1 ? relationship.person1 : relationship.person2
              return (
                <ResponsiveTableRow
                  name={relationship.relationshipId}
                  column1={
                    relationship && (
                      <PersonInfo key={associate.personId} person={associate} accessControls={accessControls} />
                    )
                  }
                  column2={relationship && GetAssociationRelationshipRole(usePerson1, relationship.associationType)}
                  column3={relationship && <OwnerId key={associate.personId} ownerInfo={associate?.ownerInfo} />}
                  column4={
                    <RelationshipLink relationshipId={relationship.relationshipId} relationshipType="association" />
                  }
                  column5={
                    <span css={floatRight}>
                      <DangerButton
                        id={`assocRemoveBtn_${relationship.relationshipId}`}
                        Icon={ContentDelete}
                        onClick={() => onRemove(relationship.relationshipId, 'association')}
                        disabled={!isAuthorized('FtWritePermission')}
                      >
                        {t('action.remove', 'Remove')}
                      </DangerButton>
                    </span>
                  }
                />
              )
            })}
        </DmcResponsiveCheckboxTable>
      )}
      <AddRelationships relationshipType="association" />
      {showTombstonedRelationships &&
        tombstonedAssociationRelationships &&
        tombstonedAssociationsRelationshipsCount > 0 && (
          <>
            <H5 growX supplementary>
              {t('person.relationships.tombstoned_plural', 'Tombstoned ({count})', {
                count: tombstonedAssociationsRelationshipsCount,
              })}
            </H5>
            {isAuthorized('FtWritePermission') &&
              tombstonedAssociationsRelationshipsCount > 0 &&
              relationshipsToRestore.length > 0 && (
                <Button
                  id="assocCollectiveRestore"
                  emphasis="medium"
                  onClick={() => handleRestoreSelected(relationshipsToRestore, 'association')}
                >
                  {t('action.restoreAssociations', 'Restore ({count}) Associations', {
                    count: relationshipsToRestore.length,
                  })}
                </Button>
              )}
            <DmcResponsiveCheckboxTable
              id="associationRestoreTable"
              totalColumnCount={5}
              onSelectionChange={setRelationshipsToRestore}
              atSizeColumns={{
                default: {
                  labels: [
                    t('relationship.role.associate', 'Associate'),
                    t('role.label', 'Role'),
                    t('owner', 'Owner'),
                    t('person.relationships.relationshipid', 'Relationship ID'),
                    '',
                  ],
                  columnWidths: [4, 2, 3, 2, 1],
                },
              }}
            >
              {tombstonedAssociationRelationships.map((relationship) => {
                const associate =
                  relationship.person1.personId === currentPerson.id ? relationship.person2 : relationship.person1
                return (
                  <ResponsiveTableRow
                    name={relationship.relationshipId}
                    column1={
                      relationship && (
                        <PersonInfo key={associate.personId} person={associate} accessControls={accessControls} />
                      )
                    }
                    column2={
                      relationship &&
                      getAssociationRelationshipRole(
                        associate.personId === relationship.person1.personId,
                        relationship.associationType,
                        t
                      )
                    }
                    column3={relationship && <OwnerId key={associate.personId} ownerInfo={associate?.ownerInfo} />}
                    column4={
                      <RelationshipLink relationshipId={relationship.relationshipId} relationshipType="association" />
                    }
                    column5={
                      <Button
                        id={`assocRestoreBtn_${relationship.relationshipId}`}
                        emphasis="medium"
                        onClick={() => onRestore(relationship.relationshipId, 'association')}
                        disabled={!isAuthorized('FtWritePermission')}
                      >
                        {t('action.restore', 'Restore')}
                      </Button>
                    }
                  />
                )
              })}
            </DmcResponsiveCheckboxTable>
          </>
        )}
      <Separator size="xl" />
    </div>
  )
}

export function getAssociationRelationshipRole(isPerson1, associationType, t) {
  switch (associationType) {
    case 'SLAVEHOLDER_TO_SLAVE':
      if (isPerson1) {
        return t('relationship.role.slaveholder', 'Slaveholder')
      }
      return t('relationship.role.enslaved', 'Enslaved Person')
    case 'ANCESTOR_TO_DESCENDANT':
      if (isPerson1) {
        return t('ancestor', 'Ancestor')
      }
      return t('relationship.role.descendant', 'Descendant')
    case 'HONORED_ANCESTOR_TO_DESCENDANT':
      if (isPerson1) {
        return t('relationship.role.honoredAncestor', 'Honored Ancestor')
      }
      return t('relationship.role.descendant', 'Descendant')
    case 'ASSOCIATE_TO_ASSOCIATE':
      return t('relationship.role.associate', 'Associate')
    case 'NEIGHBOR_TO_NEIGHBOR':
      return t('relationship.role.neighbor', 'Neighbor')
    case 'HEAD_OF_HOUSEHOLD_TO_OCCUPANT':
      if (isPerson1) {
        return t('relationship.role.head', 'Head of Household')
      }
      return t('relationship.role.occupant', 'Occupant')
    case 'GODPARENT_TO_GODCHILD':
      if (isPerson1) {
        return t('relationship.role.godparent', 'Godparent')
      }
      return t('relationship.role.godchild', 'Godchild')
    case 'EMPLOYER_TO_EMPLOYEE':
      if (isPerson1) {
        return t('relationship.role.employer', 'Employer')
      }
      return t('relationship.role.employee', 'Employee')
    case 'MASTER_TO_APPRENTICE':
      if (isPerson1) {
        return t('relationship.role.master', 'Master')
      }
      return t('relationship.role.apprentice', 'Apprentice')
    case 'RELATIVE_TO_RELATIVE':
      return t('relationship.role.relative', 'Relative')
    default:
      return t('relationship.role.associate', 'Associate')
  }
}
